import 'client-only'

import * as Fingerprint from '@fingerprint/agent'

import { CONFIG_FINGERPRINT_API_KEY, CONFIG_FINGERPRINT_ENDPOINT } from '@src/config/env'
import { getUserAgnosticGlobalFeatureGatesFromMemory } from '@src/modules/global-state/user-agnostic-feature-gates.browser'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'

import {
  getVisitorDetailsFromLocalStorage,
  getVisitorDetailsFromSessionStorage,
  LS_TTL_MS,
  reportFingerprintError,
  saveToStorage,
  type FingerprintVisitorData,
} from './fingerprint.utils'

type FingerprintAgent = ReturnType<typeof Fingerprint.start>

const MAX_GET_ATTEMPTS = 3

let agent: FingerprintAgent | null = null
let inMemVisitorDetails: FingerprintVisitorData | null = null
let getAttempts = 0

export function initializeFingerprint(lsTtlMs = LS_TTL_MS): void {
  if (!CONFIG_FINGERPRINT_API_KEY || !CONFIG_FINGERPRINT_ENDPOINT) return
  if (agent) return
  if (getAttempts >= MAX_GET_ATTEMPTS) return
  if (getVisitorDetailsFromLocalStorage() ?? getVisitorDetailsFromSessionStorage()) return

  try {
    agent = Fingerprint.start({
      apiKey: CONFIG_FINGERPRINT_API_KEY,
      region: 'us', // only us for POC - will do multi region implementation once POC passes.
      endpoints: CONFIG_FINGERPRINT_ENDPOINT,
    })
  } catch (err: unknown) {
    // start() failures are terminal config errors — jump to cap so retries stop immediately
    getAttempts = MAX_GET_ATTEMPTS
    reportFingerprintError(err)
    return
  }

  agent
    .get({
      linkedId: getlbUserId(),
    })
    .then(result => {
      if (result.visitor_id) {
        inMemVisitorDetails = saveToStorage(result, lsTtlMs)
        return
      }
      // API resolved but returned no visitor_id — treat as soft failure so the
      // retry cap kicks in and subsequent nav attempts don't silently no-op forever
      getAttempts++
      agent = null
      reportFingerprintError(new Error('fingerprint result missing visitor_id'))
    })
    .catch((err: unknown) => {
      getAttempts++
      agent = null
      reportFingerprintError(err)
    })
}

export function getFingerprintVisitorDetails(): FingerprintVisitorData | null {
  if (!CONFIG_FINGERPRINT_API_KEY || !CONFIG_FINGERPRINT_ENDPOINT) return null
  if (!getUserAgnosticGlobalFeatureGatesFromMemory().ssr_fingerprinting) return null
  if (inMemVisitorDetails) return inMemVisitorDetails

  const fromLocalStorage = getVisitorDetailsFromLocalStorage()
  if (fromLocalStorage) {
    inMemVisitorDetails = fromLocalStorage
    return fromLocalStorage
  }

  const fromSessionStorage = getVisitorDetailsFromSessionStorage()
  if (fromSessionStorage) {
    inMemVisitorDetails = fromSessionStorage
    return fromSessionStorage
  }

  return null
}
